import type { ChatMessage, RecommendationContract } from "../types";
import { shouldShowCard } from "./RecommendationCard";
import styles from "./MessageBubble.module.css";

/**
 * The card renders the spec profile + Dell shortlist visually, so the reply
 * bubble should be a short human line only. Strip every structured block the
 * model emitted (the §7 spec profile, "Why", a leaked "Budget line" label,
 * the appended Dell-laptop list, the "slide your budget" prompt).
 */
const _STRUCTURED_HEADING =
  /\n?\s*(?:[*_#>\s]*)?(?:based\s+on\s*:|(?:what[’'`]?s?\s*you[’'`]?re\s*doing|recommended\s+spec\s+profile|spec\s+profile|budget\s+line|why\s*:|tradeoff\s+note|dell laptops? (?:for|in our store)|here[’'`]?s what fits|matching dell laptops)\b)/i;

// A spec list the model wrote as markdown instead of the §7 block, e.g.
// "- **GPU**: At least 16GB VRAM". Cut there too, or it duplicates the card.
const _SPEC_BULLET =
  /\n\s*(?:[-*•]|\d+\.)\s*\**\s*(?:gpu|cpu|vram|ram|memory|storage|display|processor|graphics)\b\s*\**\s*:/i;

// Stray punctuation in front of the first real character. Mirrors
// _LEADING_JUNK in app/aero/reply_format.py — anchored and punctuation-only,
// so it can only strip what sits BEFORE the first letter or digit, never a
// period anywhere else in the sentence.
const _LEADING_JUNK = /^[\s.,;:!?*_#>—–-]+/;

function compactReply(text: string, contract?: RecommendationContract | null): string {
  const picks = contract?.product_match;
  const hasProducts = Array.isArray(picks) && picks.length > 0;

  let out = text.replace(_LEADING_JUNK, "");
  const heading = out.search(_STRUCTURED_HEADING);
  const bullet = out.search(_SPEC_BULLET);
  const marks = [heading, bullet].filter((i) => i >= 0);
  const cut = marks.length > 0 ? Math.min(...marks) : -1;
  if (cut >= 0) {
    out = out.slice(0, cut);
    // if we cut mid-thought, fall back to the last full sentence
    const stop = Math.max(
      out.lastIndexOf("."),
      out.lastIndexOf("!"),
      out.lastIndexOf("?"),
    );
    if (stop > 15 && stop < out.trimEnd().length - 1) out = out.slice(0, stop + 1);
  }
  out = out
    // any trailing "…your budget?" ask — the slider covers it
    .replace(
      /\n?\s*(?:now,?\s*)?(?:could|can|would)\s+you[^\n]*budget[^\n]*$/i,
      "",
    )
    .replace(
      /\n?\s*(?:slide|set|pick|adjust|choose|share|tell me)[^\n]*budget[^\n]*$/i,
      "",
    )
    .replace(/\n?\s*what[’'`]?s\s+your\s+budget[^\n]*$/i, "")
    .replace(/\n?\s*[*_#>\s]*budget line[*_#>:\s]*$/i, "")
    .replace(/[\s*_#>:—–-]+$/, "")
    .trim();

  if (out) return out;
  return hasProducts
    ? "Here are the Dell laptops that fit best:"
    : "Here's the setup I'd go with:";
}

export function MessageBubble({ message }: { message: ChatMessage }) {
  // Strip the structured block from the bubble whenever a card is (or will be)
  // rendered. While streaming the contract isn't known yet, but the spec /
  // laptop headings still appear in the text — cut them so the block never
  // flashes into the bubble and then "disappears" when the card takes over.
  const isAssistant = message.role === "assistant";
  const compact =
    isAssistant && (message.streaming || shouldShowCard(message.contract));

  const text = compact
    ? compactReply(message.text, message.contract)
    : message.text;

  return (
    <div className={`${styles.bubble} ${styles[message.role]}`}>
      {text}
      {message.streaming && <span className={styles.caret} aria-hidden="true" />}
    </div>
  );
}
